import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Mic, Square, Upload, Copy, Download, Pause, Play, Volume2, Cpu } from "lucide-react";
import { LANG_GROUPS, LANG_FLAGS as FLAGS, LANG_NAMES, GROUP_COLORS } from "@/config/languages";

const GROUP_ORDER = ["Global", "African", "Mzansi"];

export default function ControlPanel({
  sourceLang, setSourceLang, targetLang, setTargetLang,
  isRecording, isPaused, onStart, onStop, onPause, onResume,
  onFileUpload, onCopyAll, onExport,
  status, confidence, wordCount, duration, audioLevel,
  ttsEnabled, onTtsToggle, qcEnabled, onQcToggle,
}) {
  const fileInputRef = useRef(null);

  return (
    <div className="flex flex-col gap-4">
      {/* Language Selectors */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 block">Source Language</label>
        <Select value={sourceLang} onValueChange={setSourceLang}>
          <SelectTrigger className="w-full bg-gray-900 border-gray-700 text-white text-sm rounded">
            <SelectValue placeholder="Auto-detect" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700 text-white">
            <SelectItem value="auto">🌐 Auto-detect</SelectItem>
            {GROUP_ORDER.map(group => {
              const codes = LANG_GROUPS[group];
              if (!codes) return null;
              return (
              <div key={group}>
                <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                  <span className={`inline-block px-1.5 py-0.5 rounded ${GROUP_COLORS[group]?.bg || ''} ${GROUP_COLORS[group]?.border ? 'border ' + GROUP_COLORS[group].border : ''}`}>
                    {group}
                  </span>
                </div>
                {codes.map(code => (
                  <SelectItem key={code} value={code} className="text-sm">
                    {FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}
                  </SelectItem>
                ))}
              </div>
            );
          })}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 block">Target Language</label>
        <Select value={targetLang} onValueChange={setTargetLang}>
          <SelectTrigger className="w-full bg-gray-900 border-gray-700 text-white text-sm rounded">
            <SelectValue placeholder="Off" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700 text-white">
            <SelectItem value="off">— Off —</SelectItem>
            {GROUP_ORDER.map(group => {
              const codes = LANG_GROUPS[group];
              if (!codes) return null;
              return (
              <div key={group}>
                <div className={`px-2 py-1 text-[10px] uppercase font-bold tracking-wider ${GROUP_COLORS[group]?.text || 'text-gray-500'}`}>
                  <span className={`inline-block px-1.5 py-0.5 rounded ${GROUP_COLORS[group]?.bg || ''} ${GROUP_COLORS[group]?.border ? 'border ' + GROUP_COLORS[group].border : ''}`}>
                    {group}
                  </span>
                </div>
                {codes.filter(c => c !== sourceLang).map(code => (
                  <SelectItem key={code} value={code} className="text-sm">
                    {FLAGS[code] || "🌐"} {LANG_NAMES[code] || code}
                  </SelectItem>
                ))}
              </div>
            );
          })}
          </SelectContent>
        </Select>
      </div>

      <div className="h-px bg-gray-800" />

      {/* Record Controls */}
      <div className="grid grid-cols-2 gap-2">
        {!isRecording ? (
          <Button onClick={onStart} className="bg-cyan-600 hover:bg-cyan-500 text-white col-span-2 rounded">
            <Mic className="w-4 h-4 mr-2" /> Start Listening
          </Button>
        ) : (
          <>
            {isPaused ? (
              <Button onClick={onResume} className="bg-cyan-600 hover:bg-cyan-500 text-white rounded">
                <Play className="w-4 h-4 mr-1" /> Resume
              </Button>
            ) : (
              <Button onClick={onPause} className="bg-amber-600 hover:bg-amber-500 text-white rounded">
                <Pause className="w-4 h-4 mr-1" /> Pause
              </Button>
            )}
            <Button onClick={onStop} className="bg-red-600 hover:bg-red-500 text-white rounded">
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
          </>
        )}
      </div>

      {/* File Upload */}
      <div>
        <input ref={fileInputRef} type="file" accept="audio/*,video/*" className="hidden"
          onChange={(e) => onFileUpload && onFileUpload(e.target.files[0])} />
        <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="w-full border-gray-700 text-gray-300 hover:text-white rounded">
          <Upload className="w-4 h-4 mr-2" /> Upload Audio/Video
        </Button>
      </div>

      <div className="h-px bg-gray-800" />

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={onCopyAll} variant="outline" size="sm" className="border-gray-700 text-gray-300 rounded">
          <Copy className="w-3 h-3 mr-1" /> Copy
        </Button>
        <Button onClick={onExport} variant="outline" size="sm" className="border-gray-700 text-gray-300 rounded">
          <Download className="w-3 h-3 mr-1" /> Export
        </Button>
      </div>

      {/* TTS & QC Toggles */}
      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between bg-gray-900/60 rounded p-2.5">
          <div className="flex items-center gap-2">
            <Volume2 className="w-3.5 h-3.5 text-violet-400" />
            <label className="text-xs text-gray-300">Voice Output (TTS)</label>
          </div>
          <Switch checked={ttsEnabled} onCheckedChange={onTtsToggle}
            className="data-[state=checked]:bg-violet-600 scale-75" />
        </div>
        <div className="flex items-center justify-between bg-gray-900/60 rounded p-2.5">
          <div className="flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5 text-cyan-400" />
            <label className="text-xs text-gray-300">Quality Control</label>
          </div>
          <Switch checked={qcEnabled} onCheckedChange={onQcToggle}
            className="data-[state=checked]:bg-cyan-600 scale-75" />
        </div>
      </div>

      {/* Audio Level Meter */}
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-6 bg-gray-900 rounded overflow-hidden flex items-center px-1">
            <div className="flex items-end gap-0.5 h-4 w-full">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i}
                  className="flex-1 rounded-sm transition-all duration-75"
                  style={{
                    height: `${Math.min(100, (audioLevel || 0) * (1 + Math.sin(i * 0.8) * 0.5) * 80 + 10)}%`,
                    backgroundColor: `hsl(${180 - (audioLevel || 0) * 100}, 80%, ${50 + (audioLevel || 0) * 20}%)`,
                    opacity: isRecording ? 1 : 0.15,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-gray-900/60 rounded p-2">
          <p className="text-[10px] text-gray-500 uppercase">Confidence</p>
          <p className="text-sm font-semibold text-cyan-400">{confidence || "--"}%</p>
        </div>
        <div className="bg-gray-900/60 rounded p-2">
          <p className="text-[10px] text-gray-500 uppercase">Words</p>
          <p className="text-sm font-semibold text-white">{wordCount || 0}</p>
        </div>
        <div className="bg-gray-900/60 rounded p-2">
          <p className="text-[10px] text-gray-500 uppercase">Duration</p>
          <p className="text-sm font-semibold text-white">{duration || "00:00"}</p>
        </div>
      </div>

      {/* Status */}
      <div className="text-center">
        <span className={`inline-flex items-center gap-1.5 text-xs ${
          status === "connected" ? "text-green-400" :
          status === "connecting" ? "text-amber-400" :
          status === "error" ? "text-red-400" : "text-gray-500"
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            status === "connected" ? "bg-green-400 animate-pulse" :
            status === "connecting" ? "bg-amber-400" :
            status === "error" ? "bg-red-400" : "bg-gray-500"
          }`} />
          {status === "connected" ? "Connected" :
           status === "connecting" ? "Connecting..." :
           status === "recording" ? "Recording" :
           status === "error" ? "Error" : "Ready"}
        </span>
      </div>
    </div>
  );
}
